"use client";
import useLocalStorage, { deleteFromStorage, writeStorage } from "@rehooks/local-storage";
import { AUTH_IDENTITY_TOKEN } from "@/utils/constants/storageKeys";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import CustomerDto from "@/projectRoot/api-client/interfaces/models/customers/Customer.dto";
import { useGlobals } from "./GlobalsContext";
import { uuid } from "../utils/handlers/miscHandler";
import { useTranslations } from "use-intl";
import { useApiClient } from "./ApiClientContext";


export interface ProfileContextProps {
    identity?: CustomerDto;
    logout: () => void;
    setIdentity?: Dispatch<SetStateAction<CustomerDto>>;
    updateWishlist?: (ids: string[]) => void;
}

export const defaultContext: ProfileContextProps = { 
    identity: null,
    logout: () => {},
};

export const ProfileContext = createContext<ProfileContextProps>(defaultContext);

export type ContextProps = Partial<ProfileContextProps> & { children?: any };

export default function ProfileProvider({ children }: ContextProps) {
    const {apiClient} = useApiClient();
    const [identityTokenData] = useLocalStorage<{identity_token: string, expires_at: string}>(AUTH_IDENTITY_TOKEN);

    const [identity, setIdentity] = useState(null);
    const search = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const {toasts, setToasts} = useGlobals();
    const t = useTranslations();

    //FUNCTIONS

    const isTokenValid = (identity_token?: { expires_at: string }) => identity_token && new Date().getTime() < Date.parse(identity_token.expires_at);

    const logout = () => {
        deleteFromStorage(AUTH_IDENTITY_TOKEN);
        // sessionStorage.removeItem(STORAGE_KEY_CURRENT_CART);
        setIdentity(null);
    };

    const updateWishlist = async(ids: string[]) => {
        try{
        const response = await apiClient.auth().updateWishlist({
            ids: ids
        });
        if(response.data){
            setIdentity(response.data);
            setTimeout(() => {
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('successfull_wishlist_update')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            }, 1000);
        }
        }catch(err){
            console.log(err);
        }
    }

    const getIdentity = async() => {
        try{
            const response = await apiClient.auth().identity();
            if(response.data){
                setIdentity(response.data);
            }
        }catch(err){
            console.log(err);
        }
    }

    // EFFECTS

    useEffect(() => {
        if (!identity) {

        if(!isTokenValid(identityTokenData)){
            logout();
            return;
        }

        apiClient.auth().identity()
            .then(response => {
                if(response.data){
                    setIdentity(response.data);
                } else {
                    logout();
                }
            }).catch((err) => {
                console.log(err)
                logout();
            });
        }
    }, [identityTokenData, identity]);

    useEffect(() => {
        const token = search.get('identity_token');
        const expiresAt = search.get('expires_at');

        if (token) {
        writeStorage(AUTH_IDENTITY_TOKEN, { identity_token: token, expires_at: expiresAt });
        router.replace(pathname);
        setTimeout(() => {
            setToasts([...toasts, {
                id: uuid(),
                description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('successfull_login')}</p>`,
                initiation_time: new Date().getTime(),
                duration_time: 10000
            }]);
        }, 1000);
        // router.push(`/${lang}/auth`);
        }
    }, []);

    return (
        <ProfileContext.Provider value={{ 
        identity,
        logout,
        setIdentity,
        updateWishlist,
        }}>
        {children}
        </ProfileContext.Provider>
    )
}

export function useProfile(): ProfileContextProps {
    return useContext<ProfileContextProps>(ProfileContext);
}